/*
 * Project: Goalscape
 * File:    AddSaveModal.js
 * Path:    src/components/AddSaveModal.js
 * Description: Modal component for adding a new save (bookmark).
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement URL validation and sanitization to prevent security vulnerabilities. Consider using a service like metascraper for more reliable metadata extraction.
 */
import React, { useState, useEffect, useRef } from 'react'; // Added useEffect, useRef
import { createPortal } from 'react-dom';
import styled from 'styled-components'; // Added styled-components
import { fetchUrlMetadata } from '../utils/bookmarkUtils';
import { useCustomTabs } from '../hooks/useCustomTabs';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalTitle,
    CloseButton,
    Form,
    InputGroup,
    Label,
    Input,
    Select,
    PreviewCard,
    PreviewImage,
    PreviewContent,
    PreviewTitle,
    PreviewUrl,
    ButtonGroup,
    CancelButton,
    SaveButton,
    LoadingSpinner,
    ErrorMessage,
    InfoMessage,
    TagInputContainer, // Import the new styled component
    // TODO: Add styles for TagInput, TagButton, TagList, TagItem, ColorPicker, ColorButton
} from '../styles/AddBookmarkModal.styles';

// Predefined colors for tags
const PREDEFINED_COLORS = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#F1948A', '#82E0AA', '#D7BDE2'
];

// Styled components for suggestions (can be moved to styles file later)
const SuggestionsPanel = styled.div`
    position: absolute;
    background-color: #2c2c2c;
    border: 1px solid #444;
    border-radius: 4px;
    max-height: 150px;
    overflow-y: auto;
    z-index: 1050;
    width: 100%;
    top: 100%;
    left: 0;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
`;

const SuggestionItem = styled.div`
    padding: 8px 12px;
    cursor: pointer;
    color: #e0e0e0;
    display: flex;
    align-items: center;
    &:hover {
        background-color: #383838;
    }
`;

const AddSaveModalContent = ({ onClose, userId, allUniqueTags = [], addSave, allSaves = [] }) => {
    useBodyScrollLock();
    const { tabs, addTab } = useCustomTabs(userId);
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(null);
    const [editedTitle, setEditedTitle] = useState('');
    const [selectedTab, setSelectedTab] = useState('');
    const [newTabName, setNewTabName] = useState('');
    const [existingSaveWarning, setExistingSaveWarning] = useState(false);

    // Tag related state
    const [tagInput, setTagInput] = useState('');
    const [selectedColor, setSelectedColor] = useState(PREDEFINED_COLORS[0]);
    const [tags, setTags] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);
    const suggestionPanelRef = useRef(null);
    const tagInputRef = useRef(null); // Ref for the tag input itself

    const resetState = () => {
        setUrl('');
        setError('');
        setPreview(null);
        setLoading(false);
        setEditedTitle('');
        setTagInput('');
        setSelectedColor(PREDEFINED_COLORS[0]);
        setTags([]);
        setSuggestions([]);
        setIsSuggestionsPanelOpen(false);
        setSelectedTab('');
        setNewTabName('');
        setExistingSaveWarning(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const validateUrl = (url) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const handleUrlChange = async (e) => {
        const newUrl = e.target.value;
        setUrl(newUrl);
        setError('');
        setExistingSaveWarning(false);

        if (!newUrl) {
            setPreview(null);
            return;
        }

        if (!validateUrl(newUrl)) {
            setError('Please enter a valid URL');
            setPreview(null);
            return;
        }

        // Check if the URL is already saved
        const existingSave = allSaves.find(save => save.url === newUrl);
        if (existingSave) {
            setExistingSaveWarning(true);
            setEditedTitle(existingSave.title || '');
            setTags(existingSave.tags || []);
            setSelectedTab(existingSave.tabId || '');
            setPreview({
                url: existingSave.url,
                title: existingSave.title,
                previewImage: existingSave.previewImage
            });
            return;
        }

        try {
            setLoading(true);
            // Fetch metadata using the imported function
            const metadata = await fetchUrlMetadata(newUrl);
            setPreview({
                url: newUrl,
                title: metadata.title,
                previewImage: metadata.previewImage
            });
            setEditedTitle(metadata.title || '');
            setError('');
        } catch (err) {
            if (err.message === 'Title not found in webhook response') {
                setError('Could not extract title from URL. Please enter a title manually.');
            } else {
                setError('Error fetching URL preview');
            }
            setPreview(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!url) {
            setError('Please enter a URL');
            return;
        }

        if (!validateUrl(url)) {
            setError('Please enter a valid URL');
            return;
        }

        try {
            setLoading(true);
            let tabId = selectedTab;
            if (newTabName.trim()) {
                const newTab = await addTab(newTabName.trim());
                tabId = newTab.id;
            }

            let result;
            if (preview) {
                result = await addSave(url, {
                    title: editedTitle,
                    previewImage: preview.previewImage,
                    tags,
                    tabId,
                });
            } else {
                // If no preview, fetch metadata again
                // This path might be less common if URL input triggers preview automatically
                const metadata = await fetchUrlMetadata(url);
                result = await addSave(url, {
                    title: editedTitle || metadata.title,
                    previewImage: metadata.previewImage,
                    tags,
                    tabId,
                });
            }

            handleClose(); // Close modal first

            if (result && result.operation === 'bumped') {
                // Use a timeout to ensure alert appears after modal is visually gone or less obtrusive
                setTimeout(() => {
                    alert('URL already saved. It has been moved to the top of your list.');
                }, 100);
            }

        } catch (err) {
            // If error occurs, ensure loading is false, but don't close modal yet so user sees error
            setError('Error adding save: ' + err.message);
             // Do not call handleClose() here if you want the modal to stay open on error.
        } finally {
            setLoading(false);
        }
    };

    // Helper function to check if a tag already exists (case insensitive)
    const tagExists = (name) => tags.some(tag => tag.name.toLowerCase() === name.toLowerCase());

    const handleAddTag = () => {
        const trimmedName = tagInput.trim();
        if (trimmedName !== '') {
            if (tagExists(trimmedName)) {
                alert("This tag has already been added.");
                setTagInput('');
                setIsSuggestionsPanelOpen(false);
                return;
            }
            setTags([...tags, { name: trimmedName, color: selectedColor }]);
            setTagInput('');
            setIsSuggestionsPanelOpen(false);
            tagInputRef.current?.focus(); // Keep focus on input
        }
    };

    const handleRemoveTag = (indexToRemove) => {
        setTags(tags.filter((_, index) => index !== indexToRemove));
    };

    const handleTagInputChange = (e) => {
        const value = e.target.value;
        setTagInput(value);
        if (value.trim() === '') {
            setSuggestions([]);
            setIsSuggestionsPanelOpen(false);
        } else {
            const filtered = allUniqueTags.filter(tag =>
                tag.name.toLowerCase().includes(value.toLowerCase()) && !tagExists(tag.name)
            );
            setSuggestions(filtered);
            setIsSuggestionsPanelOpen(filtered.length > 0);
        }
    };

    const openSuggestionsPanel = () => {
        if (tagInput.trim() !== '' && !isSuggestionsPanelOpen) {
            const filtered = allUniqueTags.filter(tag =>
                tag.name.toLowerCase().includes(tagInput.trim().toLowerCase()) && !tagExists(tag.name)
            );
            if (filtered.length > 0) {
                setSuggestions(filtered);
                setIsSuggestionsPanelOpen(true);
            }
        } else if (tagInput.trim() === '') {
            setSuggestions([]);
            setIsSuggestionsPanelOpen(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        if (tagExists(suggestion.name)) {
            alert("This tag has already been added.");
            setTagInput('');
        } else {
            setTags([...tags, { name: suggestion.name, color: suggestion.color }]);
            setTagInput('');
        }
        setSuggestions([]);
        setIsSuggestionsPanelOpen(false);
        tagInputRef.current?.focus();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionPanelRef.current && !suggestionPanelRef.current.contains(event.target) &&
                tagInputRef.current && !tagInputRef.current.contains(event.target)) {
                setIsSuggestionsPanelOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const modalContent = (
        <ModalOverlay>
            <ModalContent>
                <ModalHeader>
                    <ModalTitle>Add Save</ModalTitle>
                    <CloseButton onClick={handleClose}>&times;</CloseButton>
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <InputGroup>
                        <Label htmlFor="save-url">Save URL</Label>
                        <Input
                            id="save-url"
                            type="text"
                            value={url}
                            onChange={handleUrlChange}
                            placeholder="https://example.com"
                            disabled={loading}
                            autoFocus
                        />
                        {existingSaveWarning && <InfoMessage>This URL is already saved. Saving again will move it to the top of your list.</InfoMessage>}
                        {error && <ErrorMessage>{error}</ErrorMessage>}
                    </InputGroup>

                    <InputGroup>
                        <Label htmlFor="tab-select">Add to Tab</Label>
                        <Select id="tab-select" value={selectedTab} onChange={(e) => setSelectedTab(e.target.value)}>
                            <option value="">None</option>
                            {tabs.map(tab => (
                                <option key={tab.id} value={tab.id}>{tab.name}</option>
                            ))}
                        </Select>
                        <Input
                            type="text"
                            value={newTabName}
                            onChange={(e) => setNewTabName(e.target.value)}
                            placeholder="Or create a new tab"
                        />
                    </InputGroup>

                    {/* Tag Input and Color Picker */}
                    <InputGroup>
                        <Label htmlFor="tag-input">Tags</Label>
                        <TagInputContainer>
                            {/* Input field and suggestions */}
                            <div style={{ flexGrow: 1, position: 'relative' }}> {/* Removed marginRight, handled by gap in TagInputContainer */}
                                <Input
                                    ref={tagInputRef} // Set ref
                                    id="tag-input"
                                    type="text"
                                    value={tagInput}
                                    onChange={handleTagInputChange}
                                    onFocus={openSuggestionsPanel}
                                    placeholder="Add a tag"
                                    style={{ width: '100%' }} // Ensure input takes full width of its container
                                    autoComplete="off"
                                />
                                {isSuggestionsPanelOpen && suggestions.length > 0 && (
                                    <SuggestionsPanel ref={suggestionPanelRef}>
                                        {suggestions.map((suggestion, index) => (
                                            <SuggestionItem
                                                key={index}
                                                // Use mousedown to allow click before blur closes panel
                                                onMouseDown={() => handleSuggestionClick(suggestion)}
                                            >
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    backgroundColor: suggestion.color,
                                                    marginRight: '8px'
                                                }}></span>
                                                {suggestion.name}
                                            </SuggestionItem>
                                        ))}
                                    </SuggestionsPanel>
                                )}
                            </div>
                            {/* Wrapper for color pickers and Add Tag button for mobile stacking */}
                            <div className="tag-actions-mobile-wrapper">
                                {/* Basic Color Picker */}
                                <div style={{ display: 'flex', flexShrink: 0 /*, marginRight: '10px' - removed, handled by gap */ }}>
                                    {PREDEFINED_COLORS.map(color => (
                                    <button
                                        key={color}
                                        type="button"
                                        onClick={() => setSelectedColor(color)}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            backgroundColor: color,
                                            border: selectedColor === color ? '2px solid #333' : '1px solid #ccc',
                                            marginRight: '5px', // Keep margin between color buttons
                                            cursor: 'pointer',
                                            borderRadius: '50%'
                                        }}
                                    />
                                    ))}
                                </div>
                                <button type="button" onClick={handleAddTag} /* style={{ padding: '8px 12px', whiteSpace: 'nowrap' }} - handled by styled-component or default button styles */ >
                                    Add Tag
                                </button>
                            </div>
                        </TagInputContainer>
                        {/* Display Added Tags */}
                        <div style={{ marginBottom: '10px' }}>
                            {tags.map((tag, index) => (
                                <span key={index} style={{
                                    backgroundColor: tag.color,
                                    color: 'white', // Assuming dark colors, might need a function to determine text color
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    marginRight: '5px',
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                }}>
                                    {tag.name}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTag(index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'white',
                                            marginLeft: '5px',
                                            cursor: 'pointer',
                                            fontSize: '16px'
                                        }}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>
                        {/* Display Available Tags for Quick Selection */}
                        <div style={{ marginTop: '10px', maxHeight: '150px', overflowY: 'auto' }}>
                            <Label>Available Tags:</Label>
                            {allUniqueTags.length === 0 && <span style={{fontSize: '12px', color: '#888'}}>No tags available.</span>}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {allUniqueTags.map((tag, index) => {
                                    const isAdded = tags.some(t => t.name.toLowerCase() === tag.name.toLowerCase());
                                    return (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => {
                                                if (!isAdded) {
                                                    setTags([...tags, { name: tag.name, color: tag.color }]);
                                                }
                                            }}
                                            style={{
                                                backgroundColor: tag.color,
                                                color: 'white',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                border: 'none',
                                                cursor: isAdded ? 'not-allowed' : 'pointer',
                                                opacity: isAdded ? 0.6 : 1,
                                                fontSize: '12px'
                                            }}
                                            disabled={isAdded}
                                        >
                                            {tag.name} {isAdded && <span>✓</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </InputGroup>

                    {preview && (
                        <>
                            <InputGroup>
                                <Label htmlFor="save-title">Title</Label>
                                <Input
                                    id="save-title"
                                    type="text"
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    placeholder="Enter title"
                                    disabled={loading}
                                />
                            </InputGroup>
                            <PreviewCard>
                                {preview.previewImage && (
                                    <PreviewImage
                                        src={preview.previewImage}
                                        alt={preview.title}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextElementSibling.style.display = 'flex';
                                        }}
                                    />
                                )}
                                <PreviewContent>
                                    <PreviewTitle>
                                        {editedTitle || 'Untitled Save'}
                                    </PreviewTitle>
                                    <PreviewUrl>{url}</PreviewUrl>
                                </PreviewContent>
                            </PreviewCard>
                        </>
                    )}

                    <ButtonGroup>
                        <CancelButton type="button" onClick={handleClose} disabled={loading}>
                            Cancel
                        </CancelButton>
                        <SaveButton type="submit" disabled={loading || !!error}>
                            {loading ? <LoadingSpinner /> : (existingSaveWarning ? 'Update Save' : 'Add Save')}
                        </SaveButton>
                    </ButtonGroup>
                </Form>
            </ModalContent>
        </ModalOverlay>
    );

    return createPortal(modalContent, document.body);
};


const AddSaveModal = ({ isOpen, onClose, userId, allUniqueTags = [], addSave, allSaves = [] }) => {
    if (!isOpen) {
        return null;
    }

    return <AddSaveModalContent onClose={onClose} userId={userId} allUniqueTags={allUniqueTags} addSave={addSave} allSaves={allSaves} />;
};

export default AddSaveModal;
